import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { ApiError, api } from '../lib/api';
import { Button, ConfirmDialog, Field, Input, Modal, Select, Textarea } from '../components/ui';

export interface PersonalEvent {
  id: string;
  kind: 'APPOINTMENT' | 'STUDY' | 'MEDICATION' | 'OTHER';
  title: string;
  scheduledAt: string;
  location: string | null;
  notes: string | null;
  doneAt: string | null;
}

const TIPOS: Array<{ value: PersonalEvent['kind']; label: string }> = [
  { value: 'APPOINTMENT', label: 'Un turno' },
  { value: 'STUDY', label: 'Un estudio' },
  { value: 'MEDICATION', label: 'Otra medicación' },
  { value: 'OTHER', label: 'Otra cosa' },
];

/** `datetime-local` quiere `YYYY-MM-DDTHH:mm` en hora local, no un ISO en UTC. */
function paraElCampo(fecha: Date): string {
  const local = new Date(fecha.getTime() - fecha.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

/**
 * Lo que el paciente anota en su calendario.
 *
 * Es su agenda, no la del programa: un turno con el oncólogo, un estudio, otra
 * medicación que toma. No dispara alertas, no cuenta para la adherencia y no le
 * llega a nadie más — por eso la pantalla lo dice, para que nadie anote acá algo
 * que necesita que su equipo vea.
 */
export function AgendaModal({
  open,
  evento,
  diaSugerido,
  onClose,
}: {
  open: boolean;
  /** Con uno, se está editando. Sin uno, se está creando. */
  evento: PersonalEvent | null;
  diaSugerido?: Date | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const [kind, setKind] = useState<PersonalEvent['kind']>('APPOINTMENT');
  const [title, setTitle] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [borrando, setBorrando] = useState(false);

  // Se rearma cada vez que se abre: si no, editar uno y después crear otro
  // arrastraba los datos del anterior.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setKind(evento?.kind ?? 'APPOINTMENT');
    setTitle(evento?.title ?? '');
    setLocation(evento?.location ?? '');
    setNotes(evento?.notes ?? '');
    setScheduledAt(
      paraElCampo(evento ? new Date(evento.scheduledAt) : (diaSugerido ?? new Date())),
    );
  }, [open, evento, diaSugerido]);

  const refrescar = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['me', 'treatment'] });
    onClose();
  };

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        kind,
        title: title.trim(),
        scheduledAt: new Date(scheduledAt).toISOString(),
        location: location.trim() || null,
        notes: notes.trim() || null,
      };
      return evento
        ? api.put(`/me/agenda/${evento.id}`, cuerpo)
        : api.post('/me/agenda', cuerpo);
    },
    onSuccess: refrescar,
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'No pudimos guardarlo'),
  });

  const borrar = useMutation({
    mutationFn: () => api.delete(`/me/agenda/${evento!.id}`),
    onSuccess: () => {
      setBorrando(false);
      refrescar();
    },
  });

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={evento ? 'Editar lo que anotaste' : 'Agregar a tu calendario'}
        footer={
          <div className="flex flex-wrap gap-2 justify-between w-full">
            {evento ? (
              <Button variant="ghost" onClick={() => setBorrando(true)}>
                <Trash2 className="size-4" />
                Borrar
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                loading={guardar.isPending}
                onClick={() => {
                  if (!title.trim()) return setError('Poné un nombre');
                  if (!scheduledAt) return setError('Elegí el día y la hora');
                  setError(null);
                  guardar.mutate();
                }}
              >
                Guardar
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Se dice antes de que escriba nada, no después de guardar: alguien
              que quiere avisarle algo a su equipo tiene que darse cuenta acá,
              no cuando nadie le conteste. */}
          <p className="text-sm text-fg-muted">
            Es tu agenda personal. Te sirve para acordarte, y no lo ve tu equipo de seguimiento.
          </p>

          <Field label="Qué es" htmlFor="ag-kind">
            <Select
              id="ag-kind"
              value={kind}
              onChange={(event) => setKind(event.target.value as PersonalEvent['kind'])}
            >
              {TIPOS.map((tipo) => (
                <option key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Nombre" required htmlFor="ag-title">
            <Input
              id="ag-title"
              value={title}
              autoFocus
              placeholder="Control con el oncólogo"
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>

          <Field label="Cuándo" required htmlFor="ag-when">
            <Input
              id="ag-when"
              type="datetime-local"
              value={scheduledAt}
              onChange={(event) => setScheduledAt(event.target.value)}
            />
          </Field>

          <Field label="Dónde" htmlFor="ag-where">
            <Input
              id="ag-where"
              value={location}
              placeholder="Consultorio, hospital, sala…"
              onChange={(event) => setLocation(event.target.value)}
            />
          </Field>

          <Field label="Notas" htmlFor="ag-notes">
            <Textarea
              id="ag-notes"
              rows={3}
              value={notes}
              placeholder="Llevar los últimos análisis"
              onChange={(event) => setNotes(event.target.value)}
            />
          </Field>

          {error && (
            <p className="text-sm text-danger" role="alert">
              {error}
            </p>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={borrando}
        title="Borrar de tu calendario"
        description={`"${evento?.title ?? ''}" se saca de tu calendario. Nada más se ve afectado.`}
        confirmLabel="Borrar"
        onCancel={() => setBorrando(false)}
        onConfirm={() => borrar.mutate()}
      />
    </>
  );
}
